import { useMemo } from 'react';
import { type ApplicationAgreementDataFragment } from '../../../../api/fragments/ApplicationAgreementData';
import { type KycFieldSpecsFragment } from '../../../../api/fragments/KYCFieldSpecs';
import { AssetCondition } from '../../../../api/types';
import useKYCFormValidator from '../../../../utilities/kycPresets/useKYCValidators';
import validators from '../../../../utilities/validators';
import { hasAppointmentScenario } from '../../../admin/ModuleDetailsPage/modules/implementations/shared';
import useAgreementsValidator from '../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValidator';
import { useEventJourneySetupContext } from '../Entrypoint/EventJourneySetup';

const useEventApplicantValidator = (
    applicationAgreements: ApplicationAgreementDataFragment[],
    applicationKycPresets: KycFieldSpecsFragment[]
) => {
    const { customizedFieldsInput, event } = useEventJourneySetupContext();

    const agreementValidators = useAgreementsValidator(applicationAgreements, 'agreements');
    const applicantsValidator = useKYCFormValidator(
        applicationKycPresets,
        event.module.company.countryCode,
        'customer.fields'
    );

    const customizedFieldsValidator = useMemo(
        () =>
            validators.compose(
                ...customizedFieldsInput.map((field, index) =>
                    validators.compose(
                        validators.validMaxCharacter(
                            `customizedFields[${index}].value`,
                            field.isMandatory,
                            field.characterLimit
                        )
                    )
                )
            ),
        [customizedFieldsInput]
    );

    return useMemo(
        () =>
            validators.compose(
                applicantsValidator,
                agreementValidators,
                customizedFieldsValidator,
                validators.requiredStringEnum('configuration.assetCondition', AssetCondition),
                validators.only(
                    (values, error, { prefix }) => values.configuration.tradeIn,
                    validators.forEach(
                        'tradeInVehicle',
                        validators.compose(
                            validators.requiredString('make'),
                            validators.requiredString('model'),
                            validators.requiredString('registrationNumber')
                        )
                    )
                ),
                validators.only(
                    (values, error, { prefix }) =>
                        values.configuration.testDrive &&
                        hasAppointmentScenario(event.scenarios) &&
                        event.isAllowTestDrive &&
                        event.module.__typename === 'EventApplicationModule' &&
                        event.module.appointmentModule &&
                        event.module.displayAppointmentDatepicker,
                    validators.compose(
                        validators.requiredDate('appointment.date'),
                        validators.requiredString('appointment.time')
                    )
                )
            ),
        [agreementValidators, applicantsValidator, customizedFieldsValidator, event]
    );
};

export default useEventApplicantValidator;
